import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Layout } from "../components/Layout";
import { Plus, Users, ChartLineUp, CurrencyInr, MapPin, Briefcase, ArrowRight } from "@phosphor-icons/react";

function StatCard({ label, value, sub, icon: Icon, tid }) {
  return (
    <div className="as-card p-5" data-testid={tid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-widest text-[var(--as-text-secondary)]">{label}</span>
        <Icon size={22} weight="duotone" color="#080F14" />
      </div>
      <div className="text-3xl font-extrabold mt-2 text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{value}</div>
      {sub && <div className="text-xs text-[var(--as-text-secondary)] mt-1">{sub}</div>}
    </div>
  );
}

export default function SponsorDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/sponsor/me/stats").then(({ data }) => setStats(data));
    api.get(`/sponsorship-programs?sponsor_id=${user.id}&status=`).then(({ data }) => setPrograms(data));
  }, [user]);

  if (!user) return null;
  if (user.role !== "sponsor" && user.role !== "admin")
    return <Layout><div className="p-10 text-center text-[var(--as-text-secondary)]">Sponsors only.</div></Layout>;

  const utilisation = stats && stats.slots_total > 0 ? Math.round((stats.slots_used / stats.slots_total) * 100) : 0;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-10 space-y-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--as-accent)]">Sponsor</div>
            <h1 className="text-3xl font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{user.organization_name || user.name}</h1>
            <p className="text-[var(--as-text-secondary)] mt-1">Audit-grade impact dashboard</p>
          </div>
          <Link to="/sponsorship-programs/new" className="btn-primary" data-testid="sponsor-new-program">
            <Plus size={18} weight="bold" /> Launch program
          </Link>
        </div>

        {stats && (
          <>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Programs" value={stats.programs} sub={`${stats.open_programs} open`} icon={Briefcase} tid="sp-stat-programs" />
              <StatCard label="Slots used" value={`${stats.slots_used}/${stats.slots_total}`} sub={`${utilisation}% utilisation`} icon={Users} tid="sp-stat-slots" />
              <StatCard label="Budget committed" value={`₹${(stats.budget_committed || 0).toLocaleString("en-IN")}`} sub={`₹${(stats.budget_deployed || 0).toLocaleString("en-IN")} deployed`} icon={CurrencyInr} tid="sp-stat-budget" />
              <StatCard label="Approved beneficiaries" value={stats.approved} sub={`${stats.applications} total applications`} icon={ChartLineUp} tid="sp-stat-approved" />
            </div>

            {Object.keys(stats.geo_breakdown || {}).length > 0 && (
              <div className="as-card p-6">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[var(--as-text-secondary)] mb-3">
                  <MapPin size={14} weight="duotone" /> Geographic spread
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(stats.geo_breakdown).map(([city, n]) => (
                    <span key={city} className="as-badge-skill">{city} · {n}</span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div>
          <h2 className="text-xl font-bold text-[var(--as-primary)] mb-4" style={{fontFamily:"Inter"}}>Your programs</h2>
          {programs.length === 0 ? (
            <div className="as-card p-10 text-center text-[var(--as-text-secondary)]">
              No programs yet. <Link to="/sponsorship-programs/new" className="font-bold text-[var(--as-accent)]">Launch your first one →</Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {programs.map((p) => {
                const pct = Math.round((p.slots_used / p.slots) * 100);
                return (
                  <Link to={`/sponsorship-programs/${p.id}`} key={p.id} className="as-card p-6 hover:-translate-y-1" data-testid={`sp-prog-${p.id}`}>
                    <div className="flex items-center justify-between flex-wrap">
                      <span className={`text-xs font-bold ${p.status === "open" ? "text-emerald-600" : "text-stone-500"}`}>{p.status}</span>
                      <span className="text-xs font-bold text-[var(--as-text-secondary)]">{p.slots_used}/{p.slots} slots</span>
                    </div>
                    <h3 className="text-lg font-bold mt-2 text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{p.title}</h3>
                    <div className="mt-3 h-2 bg-[var(--as-bg-alt)] rounded-full overflow-hidden">
                      <div className="h-full bg-[var(--as-accent)]" style={{width: `${pct}%`}} />
                    </div>
                    <div className="flex items-center justify-between mt-4 text-xs text-[var(--as-text-secondary)]">
                      <span>₹{p.budget_total.toLocaleString("en-IN")} committed</span>
                      <ArrowRight size={16} weight="bold" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
